import { useEffect, useMemo, useRef } from 'react';

import type { Options } from 'highcharts';

import { PaginatedMeasurementRequestParams, useGetMeasurementPaginated } from '@restDataAccess';
import { SitecontrolMeasurement, SortDirection } from '@scaleaq/sitecontrol-lib';
import HighchartsReact from 'highcharts-react-official';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import Highcharts from '@/components/chart/highcharts';

import 'highcharts/esm/modules/histogram-bellcurve.js';

import { useGetFriendlyProviderName } from '@/hooks';
import { roundToTwoDecimals, toApiDateTime } from '@/utils';

import { AutoResizeHighChart } from '../../AutoResizeHighChart.tsx';
import { useChartOptionsWithLocale, useChartTimeRange, useSensorNames } from '../../hooks';
import { HistogramChartConfigType, ICardDataState } from '../../types.ts';
import {
  bgColor,
  defaultChartOptions,
  defaultTooltipOptions,
  defaultXAxisOptions,
  defaultYAxisOptions,
  getLocaleFromLanguage,
  histogramColor,
  updateChartLocale,
} from '../defaultHighChartOptions.ts';

const options: Options = {
  ...defaultChartOptions,
  chart: {
    ...defaultChartOptions.chart,
    type: 'histogram',
    spacingBottom: 10,
  },
  legend: {
    enabled: false,
  },
  xAxis: {
    ...defaultXAxisOptions,
    type: 'linear',
  },
  tooltip: {
    formatter: undefined,
  },
};

type HistogramHighChartProps = {
  config: HistogramChartConfigType;
  setChartDataStat: (props: ICardDataState) => void;
  onChartNameChange?: (chartName: string | undefined) => void;
};

export const HistogramHighChart = ({
  config,
  setChartDataStat,
  onChartNameChange,
}: HistogramHighChartProps) => {
  const highChartsRef = useRef<HighchartsReact.RefObject>(null);
  const { t, i18n } = useTranslation(['chartConfig']);
  const { provider, sensorId } = config;
  const { getFriendlyProviderName } = useGetFriendlyProviderName();

  const sensorNames = useSensorNames();

  const { start, end, updateInterval } = useChartTimeRange(config.type, config.interval, {
    start: config.start ?? DateTime.now(),
    end: config.end ?? DateTime.now(),
  });

  //**** Getting data ****//
  const query = useMemo<PaginatedMeasurementRequestParams>(
    () => ({
      // Widget sensorId is the sensor inventory node; the measurement API names it inventoryId.
      inventoryId: sensorId,
      type: provider,
      start: toApiDateTime(start ?? DateTime.now()),
      end: end ? toApiDateTime(end) : undefined,
      sortDirection: SortDirection.Ascending,
    }),
    [sensorId, provider, start, end]
  );

  const {
    data: allMeasurements,
    isLoading,
    isError,
    isFetched,
    isRateLimited,
  } = useGetMeasurementPaginated([query], updateInterval.toMillis());

  const values = useMemo(
    () =>
      allMeasurements[provider]?.[sensorId]?.map(
        datapoint => (datapoint as SitecontrolMeasurement).value
      ) ?? [],
    [allMeasurements, provider, sensorId]
  );

  const hasMeasurements = useMemo(() => values.length !== 0, [values]);

  useEffect(() => {
    setChartDataStat({
      isLoading: isLoading,
      isError: isError,
      isEmpty: isFetched && !hasMeasurements,
      isRateLimited: isRateLimited,
    });
  }, [hasMeasurements, isError, isFetched, isLoading, isRateLimited, setChartDataStat]);

  // Update chart name in parent component
  useEffect(() => {
    const chartName =
      start && end
        ? t('chartConfig:dateRangeTooltip', {
            item: getFriendlyProviderName(provider),
            start: start.toJSDate().toLocaleDateString(),
            end: end.toJSDate().toLocaleDateString(),
          })
        : undefined;
    onChartNameChange?.(chartName);
  }, [provider, start, end, t, onChartNameChange, getFriendlyProviderName]);

  //**** Set chart options ****//
  useEffect(() => {
    const chart = highChartsRef.current?.chart;

    while (chart?.series.length) {
      chart.series[0]?.remove();
    }

    const locale = getLocaleFromLanguage(i18n.language);

    // Set locale both globally and on the chart instance to ensure it's applied
    // This is critical because existing chart instances may not pick up global setOptions changes
    Highcharts.setOptions({
      lang: {
        locale: locale,
      },
    });

    // Update the chart instance's lang property directly to ensure locale is applied
    updateChartLocale(chart, locale);

    const getAxisLabel = () => {
      const sensorName = sensorNames[sensorId] ?? sensorId;
      const providerName = getFriendlyProviderName(provider);
      return `${sensorName}: ${providerName}`;
    };

    chart?.xAxis[0]?.update({
      ...defaultXAxisOptions,
      type: 'linear',
      title: {
        ...defaultXAxisOptions.title,
        text: getAxisLabel(),
        margin: 20,
      },
    });

    chart?.yAxis[0]?.update({
      ...defaultYAxisOptions,
      title: {
        ...defaultYAxisOptions.title,
        text: undefined,
      },
    });

    if (hasMeasurements) {
      // This is a series containing the raw data for the histogram. It should not be displayed
      chart?.addSeries(
        {
          id: 'data',
          data: values,
          name: 'Raw data',
          type: 'scatter',
          visible: false,
          showInLegend: false,
        },
        false
      );

      chart?.addSeries({
        name: sensorNames[sensorId] ?? sensorId,
        type: 'histogram',
        baseSeries: 'data',
        binsNumber: 'sturges',
        color: histogramColor,
        borderColor: bgColor,
        tooltip: {
          ...defaultTooltipOptions,
          headerFormat: undefined,
          pointFormatter: function () {
            const x2 = this['x2'];
            return `[${roundToTwoDecimals(this.x)}, ${roundToTwoDecimals(x2 ?? this.x)}]<br>${t('chartConfig:histogram.count')}: <strong>${this.y}</strong>`;
          },
        },
      });
    }

    // Redraw chart to ensure locale changes are applied to existing chart instance
    chart?.redraw();
  }, [
    hasMeasurements,
    sensorId,
    sensorNames,
    provider,
    t,
    values,
    i18n,
    i18n.language,
    getFriendlyProviderName,
  ]);

  //**** Rendering ****//
  const chartOptionsWithLocale = useChartOptionsWithLocale(options);

  return (
    <AutoResizeHighChart
      highcharts={Highcharts}
      options={chartOptionsWithLocale}
      highChartsRef={highChartsRef}
    />
  );
};
